import { PgProviderAccountType, PgProviderRouteItem, PgProviderRoutesMaster } from '../../../lib/api/pgProviderRoute';
import { DEFAULT_PG_ENG_NAME_BY_PAY_METHOD_TYPE } from '../constants/labels';

export interface ResolveInput {
  payMethodId: number;
  accountType: PgProviderAccountType;
  amount: number;
}

export interface ResolveResult {
  /** 결제 요청에 실제로 실릴 값 (`{모듈코드}.{MID}`). 계정이 없으면 null */
  pgCode: string | null;
  pgProviderKorName: string | null;
  /** 채택된 규칙의 priority. 규칙 없이 코드 기본값으로 떨어졌으면 null */
  matchedPriority: number | null;
  /** 코드 기본값으로 떨어진 경우 true */
  isFallback: boolean;
}

/**
 * 편집 중인 규칙으로 결제가 어느 PG·MID 로 가는지 계산한다.
 *
 * 🔴 amondz-user-api 의 `PaymentManager.findPgCode` 와 동일한 순서로 판단해야 한다.
 * 저장 전에 결과를 보려는 것이 시뮬레이터의 목적이라, 여기서 한 줄이라도 달라지면
 * 화면이 실제와 다른 답을 보여주게 된다.
 */
export const resolveRoute = (
  routes: PgProviderRouteItem[],
  master: PgProviderRoutesMaster,
  input: ResolveInput,
): ResolveResult => {
  const payMethod = master.payMethods.find((pm) => pm.id === input.payMethodId);

  // 활성 규칙만, priority 오름차순으로 평가해 첫 매칭을 채택
  const matched = routes
    .filter((route) => route.isActive && route.payMethodId === input.payMethodId)
    .sort((a, b) => a.priority - b.priority)
    .find(
      (route) =>
        (route.accountType === null || route.accountType === input.accountType) &&
        (route.maxAmount === null || isWithinAmount(input.amount, route.maxAmount)),
    );

  const pgProviderEngName = matched
    ? master.pgProviders.find((pp) => pp.id === matched.pgProviderId)?.engName
    : payMethod && DEFAULT_PG_ENG_NAME_BY_PAY_METHOD_TYPE[payMethod.type];

  const pgProvider = master.pgProviders.find((pp) => pp.engName === pgProviderEngName);
  const account = master.pgProviderAccounts.find(
    (a) => a.pgProviderId === pgProvider?.id && a.accountType === input.accountType,
  );

  return {
    pgCode: pgProvider && account ? `${pgProvider.code}.${account.account}` : null,
    pgProviderKorName: pgProvider?.korName ?? null,
    matchedPriority: matched ? matched.priority : null,
    isFallback: !matched,
  };
};

/**
 * 금액을 알 수 없거나 0 이하면 상한 규칙에 매칭시키지 않는다.
 * (user-api `PaymentManager.isWithinAmount` 와 동일)
 */
const isWithinAmount = (amount: number, maxAmount: number): boolean =>
  Number.isFinite(amount) && amount > 0 && amount <= maxAmount;
